import React, { useEffect, useState } from "react";
import { hqApi } from "@/hq/hqApi";
import { toast } from "sonner";
import { X, Loader2, Send } from "lucide-react";

const TABS = [
  { key: "content",   label: "Content" },
  { key: "recipient", label: "Recipient" },
  { key: "preview",   label: "Preview" },
];

/**
 * FollowUpComposerModal — Phase 2E.
 *
 * Composes a plain-text staff message that the backend wraps in the
 * standard branded OrangeBySite HTML shell. Preview → Review → Send.
 * Backend stores `metadata.html` + `metadata.text` on the resulting
 * communications row so a later "View" renders exactly what was sent.
 */
export default function FollowUpComposerModal({
  open, onClose, businessId, businessName, primaryContact, primaryEmail,
  initialSubject = "", initialRelatedLabel = null,
  relatedType = null, relatedId = null,
  inReplyTo = null,
  onSent,
}) {
  const [tab, setTab] = useState("content");
  const [subject, setSubject] = useState(initialSubject || "");
  const [message, setMessage] = useState("");
  const [toEmail, setToEmail] = useState(primaryEmail || "");
  const [toName, setToName] = useState(primaryContact?.full_name || businessName || "");
  const [relatedLabel, setRelatedLabel] = useState(initialRelatedLabel || "");
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (!open) return;
    setTab("content");
    setSubject(initialSubject || "");
    setMessage("");
    setToEmail(primaryEmail || "");
    setToName(primaryContact?.full_name || businessName || "");
    setRelatedLabel(initialRelatedLabel || "");
    setPreview(null);
  }, [open, initialSubject, initialRelatedLabel, primaryEmail, primaryContact, businessName]);

  const canPreview = subject.trim().length > 0 && message.trim().length > 0 && toEmail;
  const canSend = canPreview && !sending;

  const loadPreview = async () => {
    if (!canPreview) return null;
    setBusy(true);
    try {
      const r = await hqApi.post(
        `/control/clients/${businessId}/communications/followup/preview`,
        { subject, message, to_email: toEmail, to_name: toName,
          related_type: relatedType, related_id: relatedId, related_label: relatedLabel || null },
      );
      setPreview(r.data);
      return r.data;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Preview failed");
      return null;
    } finally { setBusy(false); }
  };

  const openPreviewTab = async () => {
    await loadPreview();
    setTab("preview");
  };

  const send = async () => {
    if (!canSend) return;
    setSending(true);
    try {
      const r = await hqApi.post(
        `/control/clients/${businessId}/communications/followup/send`,
        { subject, message, to_email: toEmail, to_name: toName,
          related_type: relatedType, related_id: relatedId, related_label: relatedLabel || null,
          in_reply_to_communication_id: inReplyTo || null },
      );
      const status = r.data.status;
      if (status === "sent")        toast.success(`Follow-up sent to ${r.data.to_email}`);
      else if (status === "skipped") toast.message(`Follow-up recorded but not delivered (${r.data.send_status})`);
      else                          toast.error(`Follow-up failed to deliver (${r.data.send_status})`);
      onSent?.(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Send failed");
    } finally { setSending(false); }
  };

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" data-testid="hq-followup-modal">
      <div className="bg-white rounded-2xl w-full max-w-3xl shadow-2xl overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#EAEAEA]">
          <div>
            <div className="text-[10px] tracking-[0.28em] uppercase font-bold text-[#FF6A00]">Follow-up</div>
            <h2 className="font-black text-lg mt-1">{initialSubject ? "Follow up on this email" : "Send follow-up"}</h2>
          </div>
          <button data-testid="hq-followup-close" onClick={onClose} className="p-2 rounded-full hover:bg-[#F6F5F2]"><X size={16}/></button>
        </div>

        <div className="flex gap-1 px-6 py-3 border-b border-[#EAEAEA] bg-[#F6F5F2]">
          {TABS.map((t) => (
            <button key={t.key} data-testid={`hq-followup-tab-${t.key}`}
              onClick={() => t.key === "preview" ? openPreviewTab() : setTab(t.key)}
              className={`px-3 py-1.5 rounded-full text-[11px] font-black tracking-[0.16em] uppercase ${tab === t.key ? "bg-[#0A0A0A] text-white" : "bg-white border border-[#EAEAEA]"}`}>
              {t.label}
            </button>
          ))}
          {relatedLabel && (
            <span className="ml-auto self-center px-2 py-0.5 rounded-full bg-[#FFF7EE] border border-[#FCE1C7] font-black text-[9px] tracking-[0.16em] uppercase text-[#8A4A00]">
              Regarding · {relatedLabel}
            </span>
          )}
        </div>

        {tab === "content" && (
          <div className="p-6 space-y-3 text-sm">
            <div>
              <div className="text-[10px] tracking-[0.24em] uppercase font-bold text-[#8A8A8A] mb-1">Subject</div>
              <input data-testid="hq-followup-subject" value={subject} onChange={(e) => setSubject(e.target.value)}
                className="w-full px-3 py-2 rounded-lg border" placeholder="Just checking in"/>
            </div>
            <div>
              <div className="text-[10px] tracking-[0.24em] uppercase font-bold text-[#8A8A8A] mb-1">Message</div>
              <textarea data-testid="hq-followup-message" rows={9} value={message} onChange={(e) => setMessage(e.target.value)}
                className="w-full px-3 py-2 rounded-lg border font-sans"
                placeholder={"Hi Alex,\n\nWanted to check whether the invoice arrived and if you had any questions.\n\nLet me know when it works for you.\n"}/>
              <div className="mt-1 text-[10px] text-[#8A8A8A]">Write plain English. We wrap it in the OrangeBySite branded template.</div>
            </div>
            {relatedLabel && (
              <div>
                <div className="text-[10px] tracking-[0.24em] uppercase font-bold text-[#8A8A8A] mb-1">Related context (optional label)</div>
                <input value={relatedLabel} onChange={(e) => setRelatedLabel(e.target.value)}
                  className="w-full px-3 py-2 rounded-lg border" placeholder="Invoice INV-1004"/>
              </div>
            )}
          </div>
        )}

        {tab === "recipient" && (
          <div className="p-6 space-y-3 text-sm">
            <div>
              <div className="text-[10px] tracking-[0.24em] uppercase font-bold text-[#8A8A8A] mb-1">To name</div>
              <input data-testid="hq-followup-to-name" value={toName} onChange={(e) => setToName(e.target.value)}
                className="w-full px-3 py-2 rounded-lg border"/>
            </div>
            <div>
              <div className="text-[10px] tracking-[0.24em] uppercase font-bold text-[#8A8A8A] mb-1">To email</div>
              <input data-testid="hq-followup-to-email" type="email" value={toEmail} onChange={(e) => setToEmail(e.target.value)}
                className="w-full px-3 py-2 rounded-lg border"/>
            </div>
            <p className="text-[11px] text-[#8A8A8A]">Nothing sends until you press SEND below.</p>
          </div>
        )}

        {tab === "preview" && (
          <div className="p-4">
            {busy || !preview ? (
              <div className="p-10 flex items-center justify-center"><Loader2 size={18} className="animate-spin"/></div>
            ) : (
              <iframe title="Follow-up email" data-testid="hq-followup-preview-iframe"
                srcDoc={preview.html} className="w-full h-[520px] border border-[#EAEAEA] rounded-xl bg-white"/>
            )}
          </div>
        )}

        <div className="px-6 py-4 border-t border-[#EAEAEA] flex items-center justify-end gap-2">
          <button data-testid="hq-followup-cancel" onClick={onClose} className="px-4 py-2 rounded-full border border-[#EAEAEA] text-[11px] font-black tracking-[0.16em] uppercase">Close</button>
          <button data-testid="hq-followup-preview-btn" onClick={openPreviewTab} disabled={!canPreview}
            className={`px-4 py-2 rounded-full text-[11px] font-black tracking-[0.16em] uppercase border ${canPreview ? "border-[#0A0A0A]" : "border-[#EAEAEA] text-[#8A8A8A] cursor-not-allowed"}`}>
            Preview
          </button>
          <button data-testid="hq-followup-send" onClick={send} disabled={!canSend}
            className={`px-4 py-2 rounded-full text-[11px] font-black tracking-[0.16em] uppercase ${canSend ? "bg-[#FF6A00] text-black hover:bg-[#e75f00]" : "bg-[#F6F5F2] text-[#8A8A8A] cursor-not-allowed"}`}>
            {sending ? <Loader2 size={12} className="inline animate-spin mr-1"/> : <Send size={12} className="inline mr-1"/>}
            Send follow-up
          </button>
        </div>
      </div>
    </div>
  );
}
